import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "../components/layout/SiteLayout";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function GiveSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: null, amount: null, currency: "usd" });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: null });
      return;
    }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", payment_status: "paid", amount: data.amount, currency: data.currency });
          return;
        }
        if (data.status === "expired" || data.payment_status === "expired") {
          setState({ status: "expired", payment_status: "expired" });
          return;
        }
        if (attemptsRef.current >= 8) {
          setState({ status: "timeout", payment_status: data.payment_status });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attemptsRef.current >= 5) {
          setState({ status: "error", payment_status: null });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <SiteLayout>
      <section className="container-x py-28 lg:py-40 min-h-[60vh]">
        <div data-testid="give-success-card" className="max-w-2xl mx-auto bg-[hsl(var(--sand))] border border-[hsl(var(--line))] p-10 lg:p-14 text-center">
          {state.status === "polling" && (
            <>
              <Loader2 className="animate-spin mx-auto text-[hsl(var(--terracotta))]" size={36} />
              <h1 className="display text-3xl mt-6">Confirming your gift…</h1>
              <p className="text-sm text-[hsl(var(--ink-muted))] mt-3">Hang tight, this only takes a moment.</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <CheckCircle2 className="mx-auto text-[hsl(var(--moss))]" size={44} />
              <h1 className="display text-4xl mt-6">Thank you.</h1>
              <p className="text-[hsl(var(--ink-muted))] mt-4 leading-relaxed">
                We received your gift of <span className="text-[hsl(var(--ink))] font-medium">${Number(state.amount).toFixed(2)}</span>{" "}
                {state.currency?.toUpperCase()}. A receipt has been sent to your email if provided.
              </p>
              <Link
                to="/"
                data-testid="give-success-home"
                className="mt-10 inline-flex items-center px-7 py-3.5 rounded-sm bg-[hsl(var(--terracotta))] text-[hsl(var(--linen))] text-sm"
              >
                Back to home
              </Link>
            </>
          )}
          {(state.status === "expired" || state.status === "error" || state.status === "timeout") && (
            <>
              <XCircle className="mx-auto text-[hsl(var(--destructive))]" size={40} />
              <h1 className="display text-3xl mt-6">
                {state.status === "expired" ? "This checkout expired." : "We couldn't confirm your payment."}
              </h1>
              <p className="text-[hsl(var(--ink-muted))] mt-3">
                If you were charged, your receipt will arrive shortly. Otherwise, please try again.
              </p>
              <Link
                to="/give"
                data-testid="give-success-retry"
                className="mt-10 inline-flex items-center px-7 py-3.5 rounded-sm bg-[hsl(var(--terracotta))] text-[hsl(var(--linen))] text-sm"
              >
                Try again
              </Link>
            </>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
